import React, { useState, useEffect, useRef } from 'react';
import { X, Lock, Shield, Download, Send } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const DocumentSignModal = ({ isOpen, onClose, invoiceId, attachmentId, onSuccess, onSignAndAssign }) => {
  const [signatureMethod, setSignatureMethod] = useState('INTERNAL'); // 'INTERNAL' lub 'QUALIFIED'
  const [qualifiedProvider, setQualifiedProvider] = useState(''); // 'USB', 'CARD', 'CERTIFICATE', 'SZAFIR_SDK_WEB'
  const [downloading, setDownloading] = useState(false);
  const [szafirDetected, setSzafirDetected] = useState(false);
  // Stan dla podpisu wewnętrznego z MFA
  const [mfaCode, setMfaCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [signing, setSigning] = useState(false);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [totpSecondsRemaining, setTotpSecondsRemaining] = useState(null);
  const [totpPeriodSeconds, setTotpPeriodSeconds] = useState(30);
  const [clockDriftSeconds, setClockDriftSeconds] = useState(null);
  const [totpClockSynced, setTotpClockSynced] = useState(false);
  const totpPeriodRef = useRef(30);
  const serverOffsetMsRef = useRef(0);

  const updateTotpTick = () => {
    const serverEpochSec = Math.floor((Date.now() + serverOffsetMsRef.current) / 1000);
    const period = totpPeriodRef.current;
    const intoWindow = serverEpochSec % period;
    const remaining = period - intoWindow;
    setTotpSecondsRemaining(remaining === period ? period : remaining);
  };

  // Odliczanie TOTP wg zegara serwera (synchronizacja co ~60 s + interpolacja lokalna)
  useEffect(() => {
    if (!isOpen || signatureMethod !== 'INTERNAL') {
      setTotpSecondsRemaining(null);
      setClockDriftSeconds(null);
      setTotpClockSynced(false);
      return undefined;
    }

    let cancelled = false;
    setTotpClockSynced(false);
    updateTotpTick();

    const syncFromServer = async () => {
      try {
        const response = await api.get('/mfa/totp/clock');
        if (cancelled) return;
        const { serverTimeMillis, secondsRemaining, periodSeconds } = response.data;
        const period = periodSeconds || 30;
        totpPeriodRef.current = period;
        serverOffsetMsRef.current = serverTimeMillis - Date.now();
        setTotpSecondsRemaining(secondsRemaining);
        setTotpPeriodSeconds(period);
        setTotpClockSynced(true);
        const drift = Math.round(serverOffsetMsRef.current / 1000);
        setClockDriftSeconds(Math.abs(drift) >= 5 ? drift : null);
      } catch (err) {
        console.warn('Nie udało się pobrać zegara TOTP serwera:', err);
        if (!cancelled) {
          setTotpClockSynced(false);
          serverOffsetMsRef.current = 0;
        }
      }
    };

    syncFromServer();
    const resyncInterval = setInterval(syncFromServer, 60000);
    const tickInterval = setInterval(updateTotpTick, 1000);

    return () => {
      cancelled = true;
      clearInterval(resyncInterval);
      clearInterval(tickInterval);
    };
  }, [isOpen, signatureMethod]);

  const defaultSignReason = attachmentId
    ? 'Podpis wewnętrzny załącznika'
    : 'Podpis wewnętrzny faktury';

  // Resetuj stan MFA gdy modal się zamyka
  useEffect(() => {
    if (!isOpen) {
      setChallengeId(null);
      setMfaCode('');
    }
  }, [isOpen]);

  // Podpis wewnętrzny: od razu utwórz wyzwanie MFA (bez osobnego kroku)
  useEffect(() => {
    if (!isOpen || signatureMethod !== 'INTERNAL' || !invoiceId) {
      return undefined;
    }

    let cancelled = false;

    const createChallenge = async () => {
      setCreatingChallenge(true);
      setChallengeId(null);
      try {
        const endpoint = attachmentId
          ? `/invoices/attachments/${attachmentId}/sign/internal/challenge`
          : `/invoices/${invoiceId}/sign/internal/challenge`;
        const response = await api.post(endpoint, { authMethod: 'TOTP' });
        if (!cancelled) {
          setChallengeId(response.data.challengeId);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Błąd podczas tworzenia challenge:', error);
          toast.error(error.response?.data?.error || 'Nie udało się przygotować podpisu MFA');
        }
      } finally {
        if (!cancelled) {
          setCreatingChallenge(false);
        }
      }
    };

    createChallenge();
    return () => {
      cancelled = true;
    };
  }, [isOpen, signatureMethod, invoiceId, attachmentId]);

  useEffect(() => {
    if (signatureMethod !== 'INTERNAL') {
      setChallengeId(null);
      setMfaCode('');
    }
  }, [signatureMethod]);

  // Nasłuchuj odpowiedzi z rozszerzenia Szafir SDK Web
  useEffect(() => {
    const handleMessage = (event) => {
      // Sprawdź czy wiadomość pochodzi z rozszerzenia Szafir
      if (event.data && (
        event.data.type === 'SZAFIR_SDK_RESPONSE' ||
        event.data.type === 'SZAFIR_AVAILABLE' ||
        event.data.source === 'szafir-sdk-web' ||
        event.data.source === 'szafir-host'
      )) {
        console.log('✅ Otrzymano odpowiedź z Szafir SDK Web:', event.data);
        setSzafirDetected(true);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Sprawdź dostępność Szafir SDK Web przy załadowaniu komponentu
    const checkSzafir = async () => {
      // Metoda 1: Sprawdź standardowe obiekty window
      if (typeof window.szafir !== 'undefined' || 
          typeof window.SzafirSDK !== 'undefined' ||
          typeof window.SzafirHost !== 'undefined' ||
          typeof window.Szafir !== 'undefined' ||
          typeof document.szafir !== 'undefined') {
        setSzafirDetected(true);
        console.log('✅ Szafir SDK Web wykryte przez window object');
        return true;
      }
      
      // Metoda 2: Sprawdź custom element
      if (document.querySelector('szafir-sdk-web') !== null) {
        setSzafirDetected(true);
        console.log('✅ Szafir SDK Web wykryte przez custom element');
        return true;
      }
      
      // Metoda 3: Sprawdź przez browser.runtime.sendMessage (dla Firefox)
      // eslint-disable-next-line no-undef
      if (typeof browser !== 'undefined' && browser.runtime && browser.runtime.sendMessage) {
        try {
          console.log('🔍 Firefox wykryty - używam browser.runtime.sendMessage');
          // Szafir SDK Web extension ID dla Firefox
          const possibleExtensionIds = [
            'szafir-sdk-web@szafir.pl', // Firefox extension ID
            'szafir-sdk-web',
            'szafirhost',
            'gjalhnomhafafofonpdihihjnbafkipc'
          ];
          
          for (const extId of possibleExtensionIds) {
            try {
              // Firefox używa Promise-based API
              // eslint-disable-next-line no-undef
              const response = await browser.runtime.sendMessage(extId, { type: 'SZAFIR_SDK_CHECK' });
              
              if (response && (response.available || response.type === 'SZAFIR_AVAILABLE')) {
                setSzafirDetected(true);
                console.log('✅ Szafir SDK Web wykryte przez browser.runtime.sendMessage (Firefox):', extId);
                return true;
              }
            } catch (e) {
              // Rozszerzenie nie zainstalowane lub nie odpowiada - kontynuuj
              console.log(`⚠️ Firefox: Rozszerzenie ${extId} nie odpowiada:`, e.message);
              continue;
            }
          }
        } catch (e) {
          console.log('⚠️ browser.runtime.sendMessage nie dostępne:', e.message);
        }
      }
      
      // Metoda 4: Sprawdź przez chrome.runtime.sendMessage (dla Chrome/Edge - kompatybilność)
      // eslint-disable-next-line no-undef
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        try {
          console.log('🔍 Chrome/Edge wykryty - używam chrome.runtime.sendMessage');
          const possibleExtensionIds = [
            'gjalhnomhafafofonpdihihjnbafkipc', // Szafir SDK Web ID z Chrome Web Store
            'szafir-sdk-web',
            'szafirhost'
          ];
          
          for (const extId of possibleExtensionIds) {
            try {
              // eslint-disable-next-line no-undef
              const response = await new Promise((resolve, reject) => {
                // eslint-disable-next-line no-undef
                chrome.runtime.sendMessage(extId, { type: 'SZAFIR_SDK_CHECK' }, (response) => {
                  // eslint-disable-next-line no-undef
                  if (chrome.runtime.lastError) {
                    // eslint-disable-next-line no-undef
                    reject(chrome.runtime.lastError);
                  } else {
                    resolve(response);
                  }
                });
              });
              
              if (response && (response.available || response.type === 'SZAFIR_AVAILABLE')) {
                setSzafirDetected(true);
                console.log('✅ Szafir SDK Web wykryte przez chrome.runtime.sendMessage:', extId);
                return true;
              }
            } catch (e) {
              // Rozszerzenie nie zainstalowane lub nie odpowiada - kontynuuj
              continue;
            }
          }
        } catch (e) {
          console.log('⚠️ chrome.runtime.sendMessage nie dostępne:', e.message);
        }
      }
      
      // Metoda 5: Sprawdź przez postMessage (dla wszystkich przeglądarek)
      try {
        console.log('📨 Wysyłanie postMessage do Szafir SDK Web...');
        window.postMessage({ type: 'SZAFIR_SDK_CHECK', source: 'crm-lapy' }, '*');
        
        // Poczekaj na odpowiedź przez postMessage
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (e) {
        console.log('⚠️ postMessage error:', e.message);
      }
      
      return false;
    };
    
    checkSzafir();
    
    // Sprawdź ponownie po krótkim opóźnieniu (rozszerzenie może się ładować)
    const timeoutId = setTimeout(() => {
      checkSzafir().then(detected => {
        if (!detected) {
          // Ostatnia próba po dłuższym czasie
          setTimeout(checkSzafir, 2000);
        }
      });
    }, 1000);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
    };
  }, []);

  if (!isOpen) return null;

  // Ponowne utworzenie wyzwania MFA (np. po błędnym kodzie)
  const handleCreateChallenge = async () => {
    setCreatingChallenge(true);
    try {
      // Jeśli attachmentId jest podany, użyj endpointu dla załączników
      const endpoint = attachmentId 
        ? `/invoices/attachments/${attachmentId}/sign/internal/challenge`
        : `/invoices/${invoiceId}/sign/internal/challenge`;
      const response = await api.post(endpoint, {
        authMethod: 'TOTP'
      });
      
      setChallengeId(response.data.challengeId);
    } catch (error) {
      console.error('Błąd podczas tworzenia challenge:', error);
      toast.error(error.response?.data?.error || 'Nie udało się utworzyć wyzwania MFA');
    } finally {
      setCreatingChallenge(false);
    }
  };

  // Funkcja do podpisywania wewnętrznego z MFA
  // openAssignModal: true = "Podpisz i wyślij" (po podpisie otwórz modal przypisania), false = "Podpisz fakturę" (tylko podpisz i wróć do listy)
  const handleSignInternal = async (openAssignModal = true) => {
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error('Wprowadź 6-cyfrowy kod MFA');
      return;
    }

    if (!challengeId) {
      toast.error('Najpierw utwórz wyzwanie MFA');
      return;
    }

    setSigning(true);
    try {
      const signEndpoint = attachmentId
        ? `/invoices/attachments/${attachmentId}/sign/internal`
        : `/invoices/${invoiceId}/sign/internal`;
      const response = await api.post(signEndpoint, {
        challengeId: challengeId,
        code: mfaCode,
        reason: defaultSignReason
      });

      if (response.data.success) {
        const stamped = response.data.visualStampApplied === true;
        if (attachmentId) {
          toast.success(stamped
            ? 'Załącznik podpisany — podpis wizualny został dodany na dokumencie (PDF/skan).'
            : 'Załącznik został podpisany pomyślnie!');
        } else {
          toast.success(stamped
            ? 'Faktura podpisana — podpis wizualny został dodany na dokumencie (PDF/skan).'
            : 'Faktura została podpisana pomyślnie!');
        }
        setMfaCode('');
        setChallengeId(null);
        const signatureData = {
          signatureId: response.data.signatureId,
          signedAt: response.data.signedAt
        };
        
        if (openAssignModal && onSignAndAssign) {
          onSignAndAssign(signatureData);
        } else if (onSuccess) {
          onSuccess(signatureData);
        }
        onClose();
      }
    } catch (error) {
      console.error('Błąd podczas podpisywania:', error);
      const data = error.response?.data;
      const errorMessage = data?.error || data?.message || '';
      if (data?.unsignedAttachments?.length > 0) {
        toast.error(`${errorMessage || 'Najpierw podpisz wszystkie załączniki.'} (${data.unsignedAttachments.join(', ')})`);
        return;
      }
      
      // Sprawdź czy to błąd związany z MFA
      if (error.response?.status === 401 && 
          (errorMessage.toLowerCase().includes('nieprawidłowy kod mfa') || 
           errorMessage.toLowerCase().includes('invalid mfa code') ||
           errorMessage.toLowerCase().includes('mfa'))) {
        toast.error('Nieprawidłowy lub nieaktualny kod MFA. Wpisz aktualny kod z aplikacji i spróbuj ponownie.');
        setMfaCode('');
        await handleCreateChallenge();
      } else {
        toast.error(errorMessage || (attachmentId ? 'Nie udało się podpisać załącznika' : 'Nie udało się podpisać faktury'));
      }
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async () => {
    if (signatureMethod === 'INTERNAL') {
      await handleSignInternal(false);
      return;
    }

    if (signatureMethod === 'QUALIFIED' && !qualifiedProvider) {
      toast.error('Wybierz metodę podpisu kwalifikowanego');
      return;
    }

    setDownloading(true);
    try {
      // Pobierz plik faktury do podpisania
      const response = await api.get(`/invoices/${invoiceId}/file`, {
        responseType: 'blob',
        validateStatus: function (status) {
          // Pozwól axios obsłużyć wszystkie statusy, abyśmy mogli sprawdzić odpowiedź
          return status >= 200 && status < 500;
        }
      });

      // Sprawdź status odpowiedzi
      if (response.status === 404 || response.status !== 200) {
        // Spróbuj przeczytać błąd jako JSON (jeśli response.data jest blobem)
        let errorMessage = `Błąd podczas pobierania pliku (status: ${response.status})`;
        
        if (response.data && typeof response.data.text === 'function') {
          try {
            const text = await response.data.text();
            if (text) {
              try {
                const errorData = JSON.parse(text);
                errorMessage = errorData.error || errorData.message || errorMessage;
              } catch (e) {
                // Jeśli nie jest JSON, sprawdź czy zawiera tekst błędu
                if (text.length < 500) {
                  errorMessage = text;
                }
              }
            }
          } catch (e) {
            console.error('Błąd podczas czytania odpowiedzi błędu:', e);
          }
        }
        
        toast.error(errorMessage);
        setDownloading(false);
        return;
      }

      // Sprawdź czy odpowiedź jest poprawna
      if (!response.data) {
        toast.error('Brak danych w odpowiedzi serwera');
        setDownloading(false);
        return;
      }

      // Utwórz link do pobrania - response.data jest już blobem
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      
      // Sprawdź rozmiar bloba
      if (blob.size === 0) {
        toast.error('Pobrany plik jest pusty. Sprawdź czy faktura ma poprawny plik PDF.');
        setDownloading(false);
        return;
      }
      
      // Sprawdź Content-Type odpowiedzi
      const contentType = response.headers['content-type'] || blob.type || '';
      
      // Sprawdź czy to nie jest błąd JSON (jeśli rozmiar jest bardzo mały lub Content-Type to JSON)
      if (blob.size < 3000 || contentType.includes('application/json')) {
        try {
          const text = await blob.text();
          // Sprawdź czy zaczyna się od { lub [ (JSON)
          if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
            const errorData = JSON.parse(text);
            toast.error(errorData.error || errorData.message || 'Nie udało się pobrać pliku');
            setDownloading(false);
            return;
          }
          // Sprawdź czy zawiera typowe błędy HTML
          if (text.includes('<html') || text.includes('<!DOCTYPE')) {
            toast.error('Serwer zwrócił stronę HTML zamiast pliku PDF. Sprawdź czy faktura ma przypisany plik.');
            setDownloading(false);
            return;
          }
        } catch (e) {
          // Nie jest JSON, kontynuuj
        }
      }
      
      // Sprawdź czy to rzeczywiście PDF (sprawdź pierwsze bajty)
      if (blob.size > 0) {
        try {
          const firstBytes = await blob.slice(0, 4).arrayBuffer();
          const uint8Array = new Uint8Array(firstBytes);
          // PDF zaczyna się od %PDF
          const pdfSignature = String.fromCharCode(...uint8Array);
          if (!pdfSignature.startsWith('%PDF') && blob.size < 5000) {
            // Jeśli nie zaczyna się od %PDF i jest mały, prawdopodobnie to błąd
            const text = await blob.text();
            if (text.length < 500) {
              try {
                const errorData = JSON.parse(text);
                toast.error(errorData.error || errorData.message || 'Nie udało się pobrać pliku');
              } catch (e) {
                toast.error('Pobrany plik nie jest prawidłowym plikiem PDF. Sprawdź czy faktura ma przypisany plik.');
              }
              setDownloading(false);
              return;
            }
          }
        } catch (e) {
          console.error('Błąd podczas sprawdzania sygnatury PDF:', e);
        }
      }
      
      // Jeśli to Szafir SDK Web, spróbuj użyć API do podpisywania bezpośrednio w przeglądarce
      if (qualifiedProvider === 'SZAFIR_SDK_WEB') {
        // Funkcja pomocnicza do sprawdzania dostępności Szafir SDK Web
        const checkSzafirSDK = async () => {
          // Metoda 1: Sprawdź standardowe lokalizacje API
          if (typeof window.szafir !== 'undefined' || 
              typeof window.SzafirSDK !== 'undefined' ||
              typeof window.SzafirHost !== 'undefined' ||
              typeof window.Szafir !== 'undefined' ||
              typeof document.szafir !== 'undefined') {
            console.log('✅ Szafir SDK Web wykryte przez window object');
            return true;
          }
          
          // Metoda 2: Sprawdź przez custom element
          if (document.querySelector('szafir-sdk-web') !== null) {
            console.log('✅ Szafir SDK Web wykryte przez custom element');
            return true;
          }
          
          // Metoda 3: Sprawdź przez browser.runtime.sendMessage (dla Firefox)
          // eslint-disable-next-line no-undef
          if (typeof browser !== 'undefined' && browser.runtime && browser.runtime.sendMessage) {
            try {
              console.log('🔍 Firefox wykryty w checkSzafirSDK - używam browser.runtime.sendMessage');
              const possibleExtensionIds = [
                'szafir-sdk-web@szafir.pl', // Firefox extension ID
                'szafir-sdk-web',
                'szafirhost',
                'gjalhnomhafafofonpdihihjnbafkipc'
              ];
              
              for (const extId of possibleExtensionIds) {
                try {
                  // Firefox używa Promise-based API
                  // eslint-disable-next-line no-undef
                  const response = await browser.runtime.sendMessage(extId, { type: 'SZAFIR_SDK_CHECK' });
                  
                  if (response && (response.available || response.type === 'SZAFIR_AVAILABLE')) {
                    console.log('✅ Szafir SDK Web wykryte przez browser.runtime.sendMessage (Firefox):', extId);
                    return true;
                  }
                } catch (e) {
                  console.log(`⚠️ Firefox: Rozszerzenie ${extId} nie odpowiada:`, e.message);
                  continue;
                }
              }
            } catch (e) {
              console.log('⚠️ browser.runtime.sendMessage error:', e.message);
            }
          }
          
          // Metoda 4: Sprawdź przez chrome.runtime.sendMessage (dla Chrome/Edge)
          // eslint-disable-next-line no-undef
          if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
            try {
              console.log('🔍 Chrome/Edge wykryty w checkSzafirSDK - używam chrome.runtime.sendMessage');
              const possibleExtensionIds = [
                'gjalhnomhafafofonpdihihjnbafkipc', // Szafir SDK Web ID z Chrome Web Store
                'szafir-sdk-web',
                'szafirhost'
              ];
              
              for (const extId of possibleExtensionIds) {
                try {
                  // eslint-disable-next-line no-undef
                  const response = await new Promise((resolve, reject) => {
                    // eslint-disable-next-line no-undef
                    chrome.runtime.sendMessage(extId, { type: 'SZAFIR_SDK_CHECK' }, (response) => {
                      // eslint-disable-next-line no-undef
                      if (chrome.runtime.lastError) {
                        // eslint-disable-next-line no-undef
                        reject(chrome.runtime.lastError);
                      } else {
                        resolve(response);
                      }
                    });
                  });
                  
                  if (response && (response.available || response.type === 'SZAFIR_AVAILABLE')) {
                    console.log('✅ Szafir SDK Web wykryte przez chrome.runtime.sendMessage:', extId);
                    return true;
                  }
                } catch (e) {
                  // Rozszerzenie nie zainstalowane lub nie odpowiada - kontynuuj
                  continue;
                }
              }
            } catch (e) {
              console.log('⚠️ chrome.runtime.sendMessage error:', e.message);
            }
          }
          
          // Metoda 4: Sprawdź przez custom events
          try {
            const testEvent = new CustomEvent('szafir-check', { detail: {} });
            document.dispatchEvent(testEvent);
          } catch (e) {
            // Ignoruj błędy
          }
          
          return false;
        };
        
        // Sprawdź czy Szafir SDK Web jest dostępne
        console.log('🔍 Sprawdzanie dostępności Szafir SDK Web...');
        console.log('window.szafir:', typeof window.szafir, window.szafir);
        console.log('window.SzafirSDK:', typeof window.SzafirSDK, window.SzafirSDK);
        console.log('window.SzafirHost:', typeof window.SzafirHost, window.SzafirHost);
        console.log('window.Szafir:', typeof window.Szafir, window.Szafir);
        console.log('document.szafir:', typeof document.szafir, document.szafir);
        console.log('Custom element szafir-sdk-web:', document.querySelector('szafir-sdk-web'));
        // eslint-disable-next-line no-undef
        console.log('chrome.runtime:', typeof chrome !== 'undefined' ? typeof chrome.runtime : 'undefined');
        
        // Sprawdź wszystkie możliwe właściwości (asynchronicznie)
        let szafirAvailable = await checkSzafirSDK();
        
        // Jeśli nie znaleziono, spróbuj poczekać chwilę (rozszerzenie może się ładować)
        if (!szafirAvailable) {
          console.log('⏳ Szafir SDK Web nie wykryte natychmiast, czekam 500ms...');
          await new Promise(resolve => setTimeout(resolve, 500));
          szafirAvailable = await checkSzafirSDK();
        }
        
        // Jeśli nadal nie znaleziono, spróbuj przez message passing (dla Firefox i innych)
        if (!szafirAvailable) {
          console.log('📨 Próba komunikacji przez postMessage (Firefox)...');
          try {
            // Wyślij wiadomość do rozszerzenia
            window.postMessage({ type: 'SZAFIR_SDK_CHECK', source: 'crm-lapy' }, '*');
            
            // Poczekaj na odpowiedź (Firefox może potrzebować więcej czasu)
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Sprawdź ponownie
            szafirAvailable = await checkSzafirSDK();
          } catch (e) {
            console.log('Błąd podczas message passing:', e);
          }
        }
        
        // Ostatnia próba - sprawdź wszystkie możliwe właściwości window
        if (!szafirAvailable) {
          console.log('🔍 Ostatnia próba - sprawdzanie wszystkich właściwości window...');
          const windowKeys = Object.keys(window).filter(key => 
            key.toLowerCase().includes('szafir') || 
            key.toLowerCase().includes('sdk')
          );
          console.log('Znalezione właściwości window związane z Szafir:', windowKeys);
          
          if (windowKeys.length > 0) {
            for (const key of windowKeys) {
              console.log(`  ${key}:`, typeof window[key], window[key]);
              if (window[key] && typeof window[key] === 'object') {
                szafirAvailable = true;
                console.log(`✅ Szafir SDK Web wykryte przez właściwość window.${key}`);
                break;
              }
            }
          }
        }
        
        if (!szafirAvailable) {
          console.warn('⚠️ Szafir SDK Web nie zostało wykryte przez standardowe metody');
          
          // Zapytaj użytkownika czy rozszerzenie jest zainstalowane
          const userConfirmed = window.confirm(
            'Szafir SDK Web nie zostało wykryte automatycznie.\n\n' +
            'Jeśli masz zainstalowane rozszerzenie Szafir SDK Web:\n' +
            '1. Upewnij się, że rozszerzenie jest włączone\n' +
            '2. Przeładuj stronę (F5)\n' +
            '3. Spróbuj ponownie\n\n' +
            'Czy chcesz kontynuować i pobrać plik do podpisania lokalnie?\n' +
            '(Rozszerzenie może przechwycić plik PDF podczas otwierania)'
          );
          
          if (!userConfirmed) {
            // Pokaż instrukcję instalacji
            const installConfirmed = window.confirm(
              'Szafir SDK Web nie jest zainstalowane.\n\n' +
              'Aby użyć podpisu przez przeglądarkę:\n' +
              '1. Zainstaluj aplikację SzafirHost\n' +
              '2. Zainstaluj rozszerzenie Szafir SDK Web\n' +
              '3. Przeładuj stronę\n\n' +
              'Czy chcesz otworzyć stronę z instrukcją instalacji?'
            );
            
            if (installConfirmed) {
              window.open('https://www.elektronicznypodpis.pl/produkty/komponenty-programistyczne-sdk,452', '_blank');
            }
            
            setDownloading(false);
            return;
          }
          
          // Użytkownik potwierdził - pobierz plik (rozszerzenie może go przechwycić)
          toast('Pobieranie pliku. Jeśli masz Szafir SDK Web, może przechwycić plik podczas otwierania.', {
            icon: 'ℹ️',
            duration: 6000
          });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `faktura-${invoiceId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          toast.success(`Plik został pobrany (${(blob.size / 1024).toFixed(1)} KB). Otwórz go i podpisz używając Szafir SDK Web.`, {
            duration: 6000
          });
          
          setDownloading(false);
          return;
        }
        
        // Szafir SDK Web jest dostępne - spróbuj podpisać dokument
        try {
          toast('Inicjowanie podpisywania przez Szafir SDK Web...', { icon: 'ℹ️' });
          
          // Konwertuj blob na ArrayBuffer
          const arrayBuffer = await blob.arrayBuffer();
          
          // Sprawdź dostępne metody API
          const szafirApi = window.szafir || window.SzafirSDK || document.szafir;
          console.log('📋 Dostępne metody API Szafir:', szafirApi ? Object.keys(szafirApi) : 'brak');
          
          // Spróbuj użyć różnych wersji API Szafir SDK Web
          let signedData = null;
          let apiUsed = null;
          
          if (window.szafir) {
            console.log('🔧 Próba użycia window.szafir...');
            if (window.szafir.signPdf) {
              console.log('✅ Używam window.szafir.signPdf');
              apiUsed = 'window.szafir.signPdf';
              signedData = await window.szafir.signPdf(arrayBuffer, {
                reason: 'Podpis dokumentu',
                location: 'CRM LAPY'
              });
            } else if (window.szafir.sign) {
              console.log('✅ Używam window.szafir.sign (base64)');
              apiUsed = 'window.szafir.sign';
              const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
              signedData = await window.szafir.sign(base64, {
                reason: 'Podpis dokumentu',
                location: 'CRM LAPY'
              });
            } else if (window.szafir.signDocument) {
              console.log('✅ Używam window.szafir.signDocument');
              apiUsed = 'window.szafir.signDocument';
              signedData = await window.szafir.signDocument(arrayBuffer, {
                reason: 'Podpis dokumentu',
                location: 'CRM LAPY'
              });
            }
          }
          
          if (!signedData && window.SzafirSDK) {
            console.log('🔧 Próba użycia window.SzafirSDK...');
            if (window.SzafirSDK.signDocument) {
              console.log('✅ Używam window.SzafirSDK.signDocument');
              apiUsed = 'window.SzafirSDK.signDocument';
              signedData = await window.SzafirSDK.signDocument(arrayBuffer, {
                reason: 'Podpis dokumentu',
                location: 'CRM LAPY'
              });
            } else if (window.SzafirSDK.signPdf) {
              console.log('✅ Używam window.SzafirSDK.signPdf');
              apiUsed = 'window.SzafirSDK.signPdf';
              signedData = await window.SzafirSDK.signPdf(arrayBuffer, {
                reason: 'Podpis dokumentu',
                location: 'CRM LAPY'
              });
            }
          }
          
          if (!signedData && document.szafir) {
            console.log('🔧 Próba użycia document.szafir...');
            if (document.szafir.sign) {
              console.log('✅ Używam document.szafir.sign');
              apiUsed = 'document.szafir.sign';
              const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
              signedData = await document.szafir.sign(base64, {
                reason: 'Podpis dokumentu',
                location: 'CRM LAPY'
              });
            }
          }
          
          if (!signedData) {
            // API nie jest dostępne - pobierz plik i pozwól podpisać lokalnie
            console.warn('⚠️ Żadna metoda API Szafir SDK Web nie jest dostępna');
            throw new Error('API Szafir SDK Web nie jest dostępne. Pobieranie pliku...');
          }
          
          console.log('✅ Podpisanie zakończone, użyto API:', apiUsed);
          console.log('📦 Typ podpisanych danych:', typeof signedData, signedData instanceof ArrayBuffer ? 'ArrayBuffer' : signedData instanceof Blob ? 'Blob' : 'inny');
          
          // Jeśli podpisanie się powiodło, zapisz podpisany dokument
          if (signedData) {
            // Konwertuj podpisane dane na blob
            let signedBlob;
            if (typeof signedData === 'string') {
              // Jeśli to base64
              const binaryString = atob(signedData);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              signedBlob = new Blob([bytes], { type: 'application/pdf' });
            } else if (signedData instanceof ArrayBuffer) {
              signedBlob = new Blob([signedData], { type: 'application/pdf' });
            } else {
              signedBlob = signedData;
            }
            
            // Pobierz podpisany plik
            const url = window.URL.createObjectURL(signedBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `faktura-${invoiceId}-signed.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Dokument został podpisany przez Szafir SDK Web!', {
              duration: 5000
            });
            
            // TODO: Wyślij podpisany dokument do backendu (wymaga endpointu)
            // Na razie użytkownik może przesłać podpisany plik ręcznie
            
          } else {
            throw new Error('Brak danych podpisanych z Szafir SDK Web');
          }
          
        } catch (error) {
          console.error('Błąd podczas podpisywania przez Szafir SDK Web:', error);
          
          // Fallback - pobierz plik do podpisania lokalnie
          toast('Nie udało się podpisać przez Szafir SDK Web. Pobieranie pliku do podpisania lokalnie...', {
            icon: '⚠️',
            duration: 5000
          });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `faktura-${invoiceId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          toast.success(`Plik został pobrany (${(blob.size / 1024).toFixed(1)} KB). Otwórz go i podpisz używając Szafir SDK Web.`, {
            duration: 6000
          });
        }
        
        setDownloading(false);
        return;
      }
      
      // Pobierz plik tylko dla innych metod
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktura-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Plik został pobrany (${(blob.size / 1024).toFixed(1)} KB)`);
    } catch (error) {
      console.error('Błąd podczas pobierania pliku:', error);
      
      // Sprawdź czy to błąd 404 (plik nie istnieje)
      if (error.response?.status === 404) {
        // Spróbuj przeczytać błąd jako JSON (jeśli error.response.data jest blobem)
        let errorMessage = 'Plik faktury nie został znaleziony. Sprawdź czy faktura ma przypisany plik PDF.';
        
        if (error.response?.data && typeof error.response.data.text === 'function') {
          try {
            const text = await error.response.data.text();
            if (text) {
              try {
                const errorData = JSON.parse(text);
                errorMessage = errorData.error || errorData.message || errorMessage;
              } catch (e) {
                // Nie jest JSON, użyj domyślnego komunikatu
              }
            }
          } catch (e) {
            console.error('Błąd podczas czytania odpowiedzi błędu:', e);
          }
        }
        
        toast.error(errorMessage);
      } else if (error.response?.status === 500) {
        toast.error('Błąd serwera podczas pobierania pliku');
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Nie udało się pobrać pliku';
        toast.error(errorMessage);
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              {attachmentId ? 'Podpisz załącznik' : 'Podpisz dokument'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Wybór metody podpisu */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Wybierz metodę podpisu
            </label>
            <div className="space-y-3">
              {/* Podpis wewnętrzny */}
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  signatureMethod === 'INTERNAL'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
                onClick={() => setSignatureMethod('INTERNAL')}
              >
                <div className="flex items-start">
                  <Lock className={`h-5 w-5 mr-3 mt-0.5 flex-shrink-0 ${
                    signatureMethod === 'INTERNAL' ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1">
                      Podpis wewnętrzny
                    </div>
                    <div className="text-sm text-gray-600">
                      Podpis systemowy z identyfikacją przez konto + MFA
                    </div>
                  </div>
                </div>
              </div>

              {/* Podpis kwalifikowany */}
              <div
                className={`p-4 border-2 rounded-lg transition-all ${
                  signatureMethod === 'QUALIFIED'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div 
                  className="flex items-start cursor-pointer"
                  onClick={() => setSignatureMethod('QUALIFIED')}
                >
                  <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1">
                      Podpis kwalifikowany
                    </div>
                    <div className="text-sm text-gray-600">
                      Podpis certyfikatem kwalifikowanym
                    </div>
                  </div>
                </div>
                
                {/* Opcje podpisu kwalifikowanego - zawsze widoczne gdy QUALIFIED jest wybrane */}
                {signatureMethod === 'QUALIFIED' && (
                  <div className="mt-4 pt-4 border-t border-blue-200 space-y-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Wybierz metodę podpisu:
                    </div>
                    <label className="flex items-center cursor-pointer p-3 rounded-lg hover:bg-blue-100 transition-colors border border-transparent hover:border-blue-300">
                      <input
                        type="radio"
                        name="qualifiedProvider"
                        value="USB"
                        checked={qualifiedProvider === 'USB'}
                        onChange={(e) => setQualifiedProvider(e.target.value)}
                        className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700">USB - Certyfikat na pendrive</span>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 rounded-lg hover:bg-blue-100 transition-colors border border-transparent hover:border-blue-300">
                      <input
                        type="radio"
                        name="qualifiedProvider"
                        value="CARD"
                        checked={qualifiedProvider === 'CARD'}
                        onChange={(e) => setQualifiedProvider(e.target.value)}
                        className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700">Karta - Certyfikat na karcie chipowej</span>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 rounded-lg hover:bg-blue-100 transition-colors border border-transparent hover:border-blue-300">
                      <input
                        type="radio"
                        name="qualifiedProvider"
                        value="CERTIFICATE"
                        checked={qualifiedProvider === 'CERTIFICATE'}
                        onChange={(e) => setQualifiedProvider(e.target.value)}
                        className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700">Certyfikat - Plik certyfikatu (.p12, .pfx)</span>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 rounded-lg bg-blue-100 border-2 border-blue-400 hover:bg-blue-200 transition-colors">
                      <input
                        type="radio"
                        name="qualifiedProvider"
                        value="SZAFIR_SDK_WEB"
                        checked={qualifiedProvider === 'SZAFIR_SDK_WEB'}
                        onChange={(e) => setQualifiedProvider(e.target.value)}
                        className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-sm font-semibold text-blue-900">Szafir SDK Web - Podpis przez przeglądarkę</span>
                    </label>
                    
                    {/* Instrukcja dla wybranej metody */}
                    {qualifiedProvider && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-xs font-semibold text-blue-900 mb-2">
                          Instrukcja:
                        </div>
                        {qualifiedProvider === 'SZAFIR_SDK_WEB' ? (
                          <div className="text-xs text-blue-800">
                            Po kliknięciu "Pobierz plik do podpisania" zostanie uruchomiony proces podpisywania przez Szafir SDK Web bezpośrednio w przeglądarce.
                          </div>
                        ) : (
                          <div className="text-xs text-blue-800">
                            1. Kliknij "Pobierz plik do podpisania"<br/>
                            2. Otwórz pobrany plik PDF<br/>
                            3. Użyj oprogramowania do podpisywania (np. Adobe Acrobat, ePUAP, Autenti)<br/>
                            4. Wybierz swój certyfikat kwalifikowany<br/>
                            5. Podpisz dokument<br/>
                            6. Prześlij podpisany plik z powrotem do systemu
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* MFA + zegar TOTP serwera — podpis wewnętrzny */}
          {signatureMethod === 'INTERNAL' && (
            <div className="space-y-3">
              {totpSecondsRemaining != null && (
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="font-semibold text-blue-900">
                      {totpClockSynced ? 'Czas serwera (okno kodu TOTP)' : 'Odliczanie kodu TOTP (szacunek — łączenie z serwerem…)'}
                    </span>
                    <span
                      className={`font-mono text-lg font-bold tabular-nums ${
                        totpSecondsRemaining <= 5
                          ? 'text-red-700'
                          : totpSecondsRemaining <= 12
                            ? 'text-amber-700'
                            : 'text-blue-800'
                      }`}
                    >
                      {totpSecondsRemaining} s
                    </span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-blue-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ease-linear ${
                        totpSecondsRemaining <= 5
                          ? 'bg-red-500'
                          : totpSecondsRemaining <= 12
                            ? 'bg-amber-500'
                            : 'bg-blue-600'
                      }`}
                      style={{
                        width: `${Math.max(6, (totpSecondsRemaining / totpPeriodSeconds) * 100)}%`
                      }}
                    />
                  </div>
                  <p className="text-xs text-blue-900 mt-2">
                    {totpSecondsRemaining <= 5
                      ? 'Za chwilę nowy kod — wpisz aktualny kod z aplikacji jak najszybciej lub poczekaj na następny.'
                      : 'Wpisuj kod z Authenticatora, gdy pasek ma jeszcze kilka sekund (serwer decyduje o ważności, nie kółko w telefonie).'}
                  </p>
                  {!totpClockSynced && (
                    <p className="text-xs text-amber-900 mt-2 bg-amber-100 border border-amber-300 rounded px-2 py-1.5">
                      Nie udało się zsynchronizować z serwerem (brak endpointu lub stara wersja backendu). Odliczanie może być niedokładne — zaktualizuj CRM na serwerze.
                    </p>
                  )}
                  {clockDriftSeconds != null && (
                    <p className="text-xs text-amber-800 mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      Zegar komputera różni się od serwera o ok. {Math.abs(clockDriftSeconds)} s
                      {clockDriftSeconds > 0 ? ' (do przodu)' : ' (do tyłu)'} — włącz auto datę i godzinę na telefonie i PC.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Kod MFA (6 cyfr)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setMfaCode(value);
                  }}
                  placeholder="000000"
                  maxLength={6}
                  disabled={creatingChallenge || !challengeId}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-center text-2xl tracking-widest font-mono disabled:bg-gray-100 disabled:text-gray-500"
                  autoFocus
                />
                {creatingChallenge && (
                  <p className="text-xs text-blue-700 mt-2 flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    Przygotowywanie podpisu…
                  </p>
                )}
                {!creatingChallenge && !challengeId && (
                  <button
                    type="button"
                    onClick={handleCreateChallenge}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Ponów przygotowanie kodu MFA
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Kod z Google Authenticator, Authy itp.
                </p>
              </div>
            </div>
          )}

          {/* Przyciski */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Anuluj
            </button>
            {/* Przycisk "Podpisz i wyślij" - tylko dla podpisu wewnętrznego z challengeId */}
            {signatureMethod === 'INTERNAL' && challengeId && (
              <button
                onClick={async () => {
                  // Użyj specjalnej funkcji do podpisywania z przekierowaniem do przypisania
                  if (!mfaCode || mfaCode.length !== 6) {
                    toast.error('Wprowadź 6-cyfrowy kod MFA');
                    return;
                  }

                  setSigning(true);
                  try {
                    const signEndpoint = attachmentId
                      ? `/invoices/attachments/${attachmentId}/sign/internal`
                      : `/invoices/${invoiceId}/sign/internal`;
                    const response = await api.post(signEndpoint, {
                      challengeId: challengeId,
                      code: mfaCode,
                      reason: defaultSignReason
                    });

                    if (response.data.success) {
                      toast.success(attachmentId ? 'Załącznik został podpisany pomyślnie!' : 'Faktura została podpisana pomyślnie!');
                      setMfaCode('');
                      setChallengeId(null);
                      const signatureData = {
                        signatureId: response.data.signatureId,
                        signedAt: response.data.signedAt
                      };
                      
                      // Wywołaj callback do przypisania faktury
                      console.log('🔍 [DEBUG DocumentSignModal] Sprawdzanie callback onSignAndAssign:', {
                        hasOnSignAndAssign: !!onSignAndAssign,
                        hasOnSuccess: !!onSuccess,
                        signatureData
                      });
                      
                      if (onSignAndAssign) {
                        console.log('✅ [DEBUG DocumentSignModal] Wywołuję onSignAndAssign');
                        // Nie zamykaj modala od razu - pozwól callbackowi otworzyć modal przypisania
                        onSignAndAssign(signatureData);
                        // Zamknij modal po krótkim opóźnieniu, aby callback miał czas na otwarcie modala przypisania
                        setTimeout(() => {
                          console.log('🔒 [DEBUG DocumentSignModal] Zamykam modal podpisywania');
                          onClose();
                        }, 100);
                      } else {
                        console.warn('⚠️ [DEBUG DocumentSignModal] Brak callback onSignAndAssign, używam onSuccess');
                        if (onSuccess) {
                          onSuccess(signatureData);
                        }
                        onClose();
                      }
                    }
                  } catch (error) {
                    console.error('Błąd podczas podpisywania:', error);
                    const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
                    
                    if (error.response?.status === 401 && 
                        (errorMessage.toLowerCase().includes('nieprawidłowy kod mfa') || 
                         errorMessage.toLowerCase().includes('invalid mfa code') ||
                         errorMessage.toLowerCase().includes('mfa'))) {
                      toast.error('Nieprawidłowy lub nieaktualny kod MFA. Wpisz aktualny kod z aplikacji i spróbuj ponownie.');
                      setMfaCode('');
                      await handleCreateChallenge();
                    } else {
                      toast.error(errorMessage || (attachmentId ? 'Nie udało się podpisać załącznika' : 'Nie udało się podpisać faktury'));
                    }
                  } finally {
                    setSigning(false);
                  }
                }}
                disabled={
                  signing ||
                  creatingChallenge ||
                  !challengeId ||
                  !mfaCode ||
                  mfaCode.length !== 6
                }
                className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {signing ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Podpisywanie...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Podpisz i wyślij</span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleDownload}
              disabled={
                downloading ||
                signing ||
                creatingChallenge ||
                (signatureMethod === 'QUALIFIED' && !qualifiedProvider) ||
                (signatureMethod === 'INTERNAL' &&
                  (!challengeId || !mfaCode || mfaCode.length !== 6))
              }
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {signing ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Podpisywanie...</span>
                </>
              ) : creatingChallenge ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Tworzenie wyzwania MFA...</span>
                </>
              ) : downloading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Pobieranie...</span>
                </>
              ) : signatureMethod === 'INTERNAL' ? (
                <>
                  <Lock className="h-4 w-4" />
                  <span>Podpisz dokument</span>
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  <span>Pobierz plik do podpisania</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSignModal;
